import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Dropdown } from "react-bootstrap";
import { useConfig } from "../config";
import { cognitoLogout } from "../auth";
import { useMe } from "../App";
import ThemeToggle from "./ThemeToggle";
import BrandLogo from "./BrandLogo";
import { useFlag, FLAGS } from "../flags";

const COLLAPSE_KEY = "ld-scorecard-sidebar-collapsed";

interface NavEntry {
  to: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
  end?: boolean;
}

const NAV: NavEntry[] = [
  { to: "/", label: "Requisitions", icon: "ph-light ph-briefcase", end: true },
  { to: "/admin", label: "Admin", icon: "ph-light ph-users-three", adminOnly: true },
];

/** FlexAdmin-styled shell: fixed topbar + collapsible sidebar + main content. */
export default function AppShell({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const config = useConfig();
  const me = useMe();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop collapse is remembered per-browser; the mobile drawer stays transient.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const showThemeToggle = useFlag(FLAGS.THEME_SWITCHER);
  const showCollapse = useFlag(FLAGS.SIDEBAR_COLLAPSE);

  // Reflect our state onto <body>. `sidebar-open` is FlexAdmin's mobile drawer;
  // `sidebar-mini` is our own desktop icon-rail collapse (see index.css).
  useEffect(() => {
    document.body.classList.toggle("sidebar-open", mobileOpen);
    document.body.classList.toggle("sidebar-mini", collapsed);
  }, [mobileOpen, collapsed]);

  // Close the mobile drawer on navigation.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  // Topbar hamburger — mobile/tablet only: opens the off-canvas drawer.
  const toggleMobile = () => setMobileOpen((v) => !v);

  // Sidebar footer control — desktop only: collapse to / expand from the icon rail.
  const toggleMini = () =>
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      return next;
    });

  const signOut = async () => {
    await auth.removeUser();
    cognitoLogout(config);
  };

  const items = NAV.filter((n) => !n.adminOnly || me?.role === "admin");

  return (
    <>
      <header className="header">
        <div className="header-left">
          <Link to="/" className="header-logo d-flex align-items-center gap-2">
            <BrandLogo height={22} />
            <span className="text-muted d-none d-sm-inline" style={{ fontWeight: 600 }}>
              SE Scorecard
            </span>
          </Link>
        </div>

        <button
          className="sidebar-toggle d-xl-none"
          title="Menu"
          aria-label="Open menu"
          onClick={toggleMobile}
        >
          <i className="bi bi-list"></i>
        </button>

        <div className="header-right">
          {showThemeToggle && <ThemeToggle />}
          <Dropdown align="end">
            <Dropdown.Toggle as="button" className="user-menu" id="user-menu">
              <i className="bi bi-person-circle"></i>
              <span className="d-none d-md-inline text-truncate" style={{ maxWidth: 160 }}>
                {me?.name ?? me?.email}
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.ItemText className="small text-muted">
                {me?.email}
                {me?.role === "admin" && (
                  <span className="badge bg-info text-dark ms-2">admin</span>
                )}
              </Dropdown.ItemText>
              <Dropdown.Divider />
              <Dropdown.Item onClick={signOut}>
                <i className="bi bi-box-arrow-right me-2"></i>Sign out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>

      <aside className="sidebar">
        <nav className="sidebar-nav">
          <ul className="nav-menu">
            {items.map((n) => (
              <li className="nav-item" key={n.to}>
                <NavLink
                  to={n.to}
                  end={n.end}
                  title={n.label}
                  className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
                >
                  <span className="nav-icon">
                    <i className={n.icon}></i>
                  </span>
                  <span className="nav-text">{n.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="sidebar-footer d-flex flex-column gap-2">
          <button
            className="btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
            onClick={signOut}
            title="Sign out"
          >
            <i className="bi bi-box-arrow-right"></i>
            <span className="mini-hide ms-2">Sign out</span>
          </button>
          {/* Desktop-only collapse control (moved out of the topbar). */}
          {showCollapse && (
          <button
            className="btn btn-sm btn-light w-100 d-none d-xl-flex align-items-center justify-content-center collapse-toggle"
            onClick={toggleMini}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <i className={`bi ${collapsed ? "bi-chevron-double-right" : "bi-chevron-double-left"}`}></i>
            <span className="mini-hide ms-2">Collapse</span>
          </button>
          )}
        </div>
      </aside>

      <div className="sidebar-overlay" onClick={() => setMobileOpen(false)}></div>

      <main className="main">
        <div className="main-content">{children}</div>
      </main>
    </>
  );
}
